import { useNavigate } from "react-router-dom";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import {
  ArrowRight01Icon,
  InformationCircleIcon,
  RefreshIcon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  Agreement01Icon,
  Alert02Icon,
  AlertCircleIcon,
  Bathtub01Icon,
  Bone01Icon,
  Building03Icon,
  Camera01Icon,
  CheckmarkCircle02Icon,
  Contact01Icon,
  DollarCircleIcon,
  Invoice01Icon,
  Location01Icon,
  Note01Icon,
  Plug01Icon,
  RulerIcon,
  SafeIcon,
  Sofa01Icon,
  Video01Icon
} from "@hugeicons-pro/core-solid-rounded";
import {
  BadgeSize,
  Button,
  ButtonSize,
  ButtonVariant,
  Show
} from "shared/components";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { cx } from "shared/utils";
import type {
  ListingHealth,
  ListingHealthIssueCode,
  ListingHealthIssueKind,
  ListingHealthLabel
} from "types/landlord/address";
import { ListingHealthBadge } from "shared/app/ListingHealth/ListingHealthBadge";
import {
  LISTING_HEALTH_HEADLINE,
  LISTING_HEALTH_NOTHING_TO_IMPROVE,
  listingHealthSyncText
} from "shared/app/ListingHealth/constants";
import {
  buildListingHealthLines,
  type ListingHealthFix,
  type ListingHealthLine
} from "shared/app/ListingHealth/issues";
import {
  propertySectionPath,
  revealSection,
  unitSectionPath
} from "shared/app/ListingHealth/navigation";

const LABEL_ICON: Record<
  ListingHealthLabel,
  { icon: IconSvgElement; className: string }
> = {
  weak: { icon: Alert02Icon, className: "bg-danger-50 text-danger-600" },
  fair: { icon: AlertCircleIcon, className: "bg-warning-50 text-warning-600" },
  strong: {
    icon: CheckmarkCircle02Icon,
    className: "bg-success-50 text-success-700"
  }
};

const KIND: Record<
  ListingHealthIssueKind,
  { iconClass: string; bubble: string; meaning: string }
> = {
  must: {
    iconClass: "text-danger-600",
    bubble: "bg-danger-50",
    meaning: "Listing sites need this: the listing is Weak until it is there"
  },
  short: {
    iconClass: "text-warning-600",
    bubble: "bg-warning-50",
    meaning: "Would rank the listing higher"
  },
  tip: {
    iconClass: "text-content-500",
    bubble: "bg-background-800",
    meaning: "A tip: doesn't change the label"
  }
};

const TOPIC_ICON: Record<ListingHealthIssueCode, IconSvgElement> = {
  photos: Camera01Icon,
  description: Note01Icon,
  amenities: Sofa01Icon,
  rent: DollarCircleIcon,
  address: Location01Icon,
  "property-type": Building03Icon,
  baths: Bathtub01Icon,
  contact: Contact01Icon,
  sqft: RulerIcon,
  deposit: SafeIcon,
  "application-fee": Invoice01Icon,
  "pet-fee": Bone01Icon,
  utilities: Plug01Icon,
  "lease-terms": Agreement01Icon,
  tour: Video01Icon
};

const FixButton = ({
  fix,
  onFix
}: {
  fix: ListingHealthFix;
  onFix: (fix: ListingHealthFix) => void;
}) => (
  <Button
    variant={ButtonVariant.TEXT}
    size={ButtonSize.SM}
    HugeIcon={ArrowRight01Icon}
    iconPosition="right"
    className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap !px-2 opacity-80 transition-opacity duration-200 group-hover:opacity-100"
    title={
      fix.target === "unit"
        ? `Go to ${fix.section}`
        : `Opens the Property page → ${fix.section}`
    }
    onClick={(event) => {
      event.stopPropagation();
      onFix(fix);
    }}
  >
    {fix.target === "unit" ? "Fix" : "Open property"}
  </Button>
);

const GridCard = ({
  line,
  onFix
}: {
  line: ListingHealthLine;
  onFix: (fix: ListingHealthFix) => void;
}) => {
  const kind = KIND[line.kind];
  const { fix } = line;
  return (
    <li
      className={cx(
        "group flex flex-col rounded-lg border border-alpha-100 bg-content-1000-inverted p-4 shadow-xs transition-all duration-200",
        fix
          ? "cursor-pointer hover:-translate-y-0.5 hover:border-alpha-200 hover:shadow-md"
          : ""
      )}
      onClick={fix ? () => onFix(fix) : undefined}
    >
      <div className="flex items-start justify-between gap-3">
        <span
          className={cx(
            "flex size-9 shrink-0 items-center justify-center rounded-[8px]",
            kind.bubble
          )}
          title={kind.meaning}
        >
          <HugeiconsIcon
            icon={TOPIC_ICON[line.code]}
            size={18}
            className={kind.iconClass}
          />
        </span>
        {fix ? <FixButton fix={fix} onFix={onFix} /> : null}
      </div>
      <p
        className={cx(
          "mt-3 text-sm font-semibold leading-5",
          line.kind === "tip" ? "text-content-700" : "text-content-1000"
        )}
      >
        {line.title}
      </p>
      <Show if={Boolean(line.detail)}>
        <p className="mt-1 text-sm font-medium leading-relaxed text-content-400">
          {line.detail}
        </p>
      </Show>
    </li>
  );
};

const ToImproveRow = ({ pmsName }: { pmsName: string | null }) =>
  pmsName ? (
    <Tooltip placement="top-end">
      <TooltipTrigger asChild>
        <div className="group/sync -mx-2 mt-4 mb-1 flex cursor-default items-center justify-between gap-4 rounded-md px-2 py-1.5 transition-colors duration-200 hover:bg-alpha-40">
          <p className="text-xs font-bold uppercase tracking-wide text-content-400">
            To improve
          </p>
          <span className="flex items-center gap-1.5 text-xs font-medium text-content-400 transition-colors duration-200 group-hover/sync:text-content-700">
            <HugeiconsIcon icon={RefreshIcon} size={14} className="shrink-0" />
            <span>Syncs from {pmsName}</span>
            <HugeiconsIcon
              icon={InformationCircleIcon}
              size={14}
              className="shrink-0"
            />
          </span>
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-[300px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-content-1000-inverted">
        <p>{listingHealthSyncText(pmsName)}</p>
      </TooltipContent>
    </Tooltip>
  ) : (
    <div className="mt-5 mb-2 flex items-center justify-between gap-4">
      <p className="text-xs font-bold uppercase tracking-wide text-content-400">
        To improve
      </p>
      <p className="text-xs text-content-400">Worst first</p>
    </div>
  );

export const ListingHealthPanel = ({
  health,
  propertyId,
  unitId
}: {
  health: ListingHealth;
  propertyId: number;
  unitId: number;
}) => {
  const navigate = useNavigate();
  const lines = buildListingHealthLines(health);
  const nothingToImprove = lines.length === 0;
  const labelIcon = LABEL_ICON[health.label];

  const handleFix = (fix: ListingHealthFix) => {
    if (fix.target === "property") {
      navigate(propertySectionPath(propertyId, fix.section));
      return;
    }
    // The single-family Property page carries only some of the unit's sections.
    if (!revealSection(fix.section))
      navigate(unitSectionPath(propertyId, unitId, fix.section));
  };

  return (
    <div className="border-b border-alpha-40 pb-6">
      <div className="flex items-start gap-3">
        <span
          className={cx(
            "flex size-10 shrink-0 items-center justify-center rounded-[8px]",
            labelIcon.className
          )}
        >
          <HugeiconsIcon icon={labelIcon.icon} size={20} />
        </span>
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-base font-bold leading-6 text-content-1000">
              Listing health
            </h4>
            <ListingHealthBadge
              label={health.label}
              size={BadgeSize.SM}
              clickable={false}
            />
          </div>
          <p className="mt-0.5 text-sm font-medium text-content-400">
            {nothingToImprove
              ? LISTING_HEALTH_NOTHING_TO_IMPROVE
              : LISTING_HEALTH_HEADLINE[health.label]}
          </p>
        </div>
      </div>

      <Show if={!nothingToImprove}>
        <ToImproveRow pmsName={health.pms_name ?? null} />
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {lines.map((line) => (
            <GridCard key={line.code} line={line} onFix={handleFix} />
          ))}
        </ul>
      </Show>
    </div>
  );
};
